// Gemerkte Anmeldung und Biometrie in der nativen App.
//
// Der Ablauf:
//   Erstanmeldung  Benutzername und Passwort wie immer. Danach fragt der
//                  Server nach einem Gerätemerkmal und die App legt es in den
//                  Schlüsselbund des Telefons.
//   Jeder Start    Fingerabdruck oder Gesicht → Merkmal aus dem Schlüsselbund
//                  → frische Sitzung. Kein Passwort, kein Tippen.
//   Rückkehr       Nach längerer Pause im Hintergrund wieder Biometrie.
//
// Das Passwort wird NIE gespeichert. Im Schlüsselbund liegt nur ein Merkmal,
// das für dieses eine Gerät gilt und jederzeit aus der Ferne abgemeldet
// werden kann.
//
// Alle Capacitor-Aufrufe laufen über dynamische Importe. So wandert nichts
// davon in den Browser-Build — dort verhält sich die Anwendung wie bisher.
import { istNativ, plattform } from '../../../gemeinsam/lib/plattform';

// Bewusst das Paket von Capgo und nicht das gleichnamige Original: Capacitor 8
// bindet iOS-Plugins über den Swift Package Manager ein, und das Original
// bringt nur eine CocoaPods-Beschreibung mit. Es wurde beim Bauen STILL
// weggelassen — die App lief, nur Biometrie und Schlüsselbund fehlten
// ersatzlos. Die Schnittstelle ist dieselbe.

/** Name des Eintrags im Schlüsselbund. */
const SCHLUESSELBUND = 'lagerpro.geraet';
/** Platzhalter: der Schlüsselbund will ein Paar, wir brauchen nur das Merkmal. */
const KENNUNG = 'geraet';

/** Wie lange die App im Hintergrund sein darf, bevor neu geprüft wird. */
export const SPERRE_NACH_MS = 5 * 60 * 1000;

const SCHALTER = 'lp_biometrie';

export type BiometrieArt = 'faceId' | 'touchId' | 'fingerabdruck' | 'andere' | 'keine';

// ---------------------------------------------------------------------------
// Verfügbarkeit
// ---------------------------------------------------------------------------

type BiometrieModul = typeof import('@capgo/capacitor-native-biometric');

async function modul(): Promise<BiometrieModul['NativeBiometric'] | null> {
  if (!istNativ()) return null;
  try {
    const m = await import('@capgo/capacitor-native-biometric');
    return m.NativeBiometric;
  } catch {
    return null;
  }
}

/** Was das Gerät kann — für die Beschriftung in den Einstellungen. */
export async function biometrieArt(): Promise<BiometrieArt> {
  const nb = await modul();
  if (!nb) return 'keine';
  try {
    const ergebnis = await nb.isAvailable({ useFallback: true });
    if (!ergebnis.isAvailable) return 'keine';
    // Die Kennungen des Herstellers sind Zahlen; nur die beiden, die man in
    // der Oberfläche beim Namen nennen will, werden unterschieden.
    const art = String(ergebnis.biometryType);
    if (art === '1' || /face/i.test(art)) return 'faceId';
    if (art === '2' || /touch/i.test(art)) return 'touchId';
    if (/finger/i.test(art)) return 'fingerabdruck';
    return 'andere';
  } catch {
    return 'keine';
  }
}

export async function biometrieVerfuegbar(): Promise<boolean> {
  return (await biometrieArt()) !== 'keine';
}

/** Hat der Benutzer die Sperre eingeschaltet? Standard: ja, wenn möglich. */
export function biometrieAn(): boolean {
  try { return localStorage.getItem(SCHALTER) !== 'aus'; } catch { return true; }
}

export function biometrieSetzen(an: boolean) {
  try { localStorage.setItem(SCHALTER, an ? 'an' : 'aus'); } catch { /* egal */ }
}

// ---------------------------------------------------------------------------
// Schlüsselbund
// ---------------------------------------------------------------------------

/** Liegt ein gemerktes Gerät vor? */
export async function merkmalVorhanden(): Promise<boolean> {
  const nb = await modul();
  if (!nb) return false;
  try {
    const d = await nb.getCredentials({ server: SCHLUESSELBUND });
    return !!d?.password;
  } catch {
    return false;
  }
}

export async function merkmalSpeichern(merkmal: string): Promise<boolean> {
  const nb = await modul();
  if (!nb) return false;
  try {
    await nb.setCredentials({ username: KENNUNG, password: merkmal, server: SCHLUESSELBUND });
    return true;
  } catch {
    return false;
  }
}

export async function merkmalLoeschen(): Promise<void> {
  const nb = await modul();
  if (!nb) return;
  try { await nb.deleteCredentials({ server: SCHLUESSELBUND }); } catch { /* war keines da */ }
}

/**
 * Fragt nach Fingerabdruck oder Gesicht und gibt danach das Merkmal zurück.
 *
 * Die Prüfung steht VOR dem Lesen: der Schlüsselbund selbst gibt den Eintrag
 * der App ohne Weiteres heraus, die Hürde ist die Identitätsprüfung. Ohne sie
 * wäre ein unbeaufsichtigtes, entsperrtes Telefon ein offener Zugang.
 */
export async function merkmalHolen(grund: string): Promise<
  { ok: true; merkmal: string } | { ok: false; grund: 'abgebrochen' | 'keines' | 'fehler' }
> {
  const nb = await modul();
  if (!nb) return { ok: false, grund: 'keines' };

  if (biometrieAn()) {
    try {
      await nb.verifyIdentity({
        reason: grund,
        title: 'LagerPro',
        subtitle: grund,
        // Wer keine Biometrie eingerichtet hat, kommt über die Gerätesperre
        // herein — sonst wäre die App für ihn unbenutzbar.
        useFallback: true,
        fallbackTitle: 'Gerätecode verwenden',
      });
    } catch {
      return { ok: false, grund: 'abgebrochen' };
    }
  }

  try {
    const d = await nb.getCredentials({ server: SCHLUESSELBUND });
    if (!d?.password) return { ok: false, grund: 'keines' };
    return { ok: true, merkmal: d.password };
  } catch {
    return { ok: false, grund: 'keines' };
  }
}

/** Nur prüfen, ohne etwas zu lesen — für das Entsperren einer laufenden Sitzung. */
export async function identitaetPruefen(grund: string): Promise<boolean> {
  const nb = await modul();
  if (!nb) return true;
  if (!biometrieAn()) return true;
  try {
    await nb.verifyIdentity({
      reason: grund, title: 'LagerPro', subtitle: grund,
      useFallback: true, fallbackTitle: 'Gerätecode verwenden',
    });
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Gerätename
// ---------------------------------------------------------------------------

/**
 * Wie das Gerät in der Liste heißen soll. Der Benutzer soll sein eigenes
 * Telefon wiedererkennen, wenn er irgendwann eines abmelden muss.
 */
export async function geraeteAngaben(): Promise<{
  name: string; plattform: string; modell: string; version: string;
}> {
  const p = plattform();
  let modell = '';
  let name = p === 'ios' ? 'iPhone' : p === 'android' ? 'Android-Gerät' : 'Browser';
  try {
    const { Device } = await import('@capacitor/device');
    const info = await Device.getInfo();
    modell = [info.manufacturer, info.model].filter(Boolean).join(' ');
    name = info.name || modell || name;
  } catch {
    // Ohne das Geräte-Modul bleibt es bei der groben Bezeichnung.
  }
  let version = '';
  try {
    const { App } = await import('@capacitor/app');
    version = (await App.getInfo()).version;
  } catch { /* im Browser nicht vorhanden */ }

  return { name, plattform: p, modell, version };
}
